import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useHeaderTabs } from '@e3dc-react/shell/Libs/Header'
import { ServiceDashboardTabs } from '../types/default'
import { useUserRoles } from 'src/Hooks/user'
import { isFlowTheme } from 'src/Libs/utils'

interface UseServiceDashboardTabs {
  tabs: ServiceDashboardTabs[]
  setTabs: (value: ServiceDashboardTabs[]) => void
  setActiveTab: (value: number) => void
}

export const useServiceDashboardTabs = (canSetTabs = true): UseServiceDashboardTabs => {
  const navigate = useNavigate()
  const location = useLocation()
  const { setTabs, setActiveTab } = useHeaderTabs()
  const { t, selectedAppLanguage } = useCustomTranslation()
  const { isServiceUser } = useUserRoles()
  const disableTabsTemporarilyForFlowTheme = isFlowTheme

  const tabs = useMemo(
    () => [
      {
        label: t('serviceDashboard.tabs.installation'),
        href: '/overview/installations',
        dataQa: 'tab-serviceDashboard-installations',
        onClick: (href?: string) => {
          if (href) {
            navigate(href)
          }
        },
      },
      {
        label: t('serviceDashboard.tabs.user'),
        href: '/overview/users',
        dataQa: 'tab-serviceDashboard-users',
        disabled: disableTabsTemporarilyForFlowTheme,
        onClick: (href?: string) => {
          if (href) {
            navigate(href)
          }
        },
      },
      {
        label: t('serviceDashboard.tabs.company'),
        href: '/overview/companies',
        dataQa: 'tab-serviceDashboard-companies',
        disabled: disableTabsTemporarilyForFlowTheme,
        onClick: (href?: string) => {
          if (href) {
            navigate(href)
          }
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAppLanguage, disableTabsTemporarilyForFlowTheme]
  )

  useEffect(() => {
    if (isServiceUser && canSetTabs && (location.pathname.startsWith('/overview/') || location.pathname.startsWith('/create-user'))) {
      setTabs(tabs)
    }
    return () => setTabs([])
  }, [tabs, setTabs, canSetTabs, location.pathname, isServiceUser])

  return { tabs, setTabs, setActiveTab }
}
